import React, { useState, useEffect, useCallback } from 'react';
import { Text, TextInput, Button, CircularProgress, makeStyles } from 'design-system';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import * as QueryString from 'query-string';
import { RouteComponentProps } from 'react-router-dom';
import SAlert from 'react-s-alert';

import {
  UpdateGuidedWorkflowRequest,
  GuidedWorkflowDetails,
} from '@scale/shared/guidedWorkflow/types/api';
import { updateGWF, createGWF, getGWF } from './sdk';
import { requiredAsterisk } from 'react-scale/corp/helpers';

const useStyles = makeStyles({
  base: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    flexShrink: 1,
    marginBottom: '5px',
  },
  errors: {},
  footer: {
    flexShrink: 1,
    marginTop: '5px',
  },
  markdownContainer: {
    flexGrow: 1,
    display: 'flex',
    gap: '12px',
    '& textarea': {
      height: '100%',
      width: '100%',
      resize: 'none',
    },
    overflow: 'hidden',
  },
  markdownEditor: {
    height: '100%',
    flex: 1,
  },
  markdownRenderer: {
    height: '100%',
    flex: 1,
    overflowY: 'scroll',
    borderStyle: 'solid',
    border: '1px',
    padding: '0px 6px',
  },
});

const GuidedWorkflowEditor: React.FC<RouteComponentProps> = props => {
  const queryParams = QueryString.parse(props.location.search);
  const gwfId = queryParams.gwfId as string | undefined;

  const classes = useStyles();

  const [gwf, setGWF] = useState<GuidedWorkflowDetails>();
  const [gwfMarkdown, setGWFMarkdown] = useState<string>();
  const [gwfName, setGWFName] = useState<string>();
  const [errors, setErrors] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const loadGWF = useCallback(
    (gwfId: string) => {
      const refresh = async () => {
        setIsLoading(true);
        const gwf = await getGWF(gwfId);
        if (gwf) {
          setGWF(gwf);
          if (gwf.definition.type === 'markdownWorkflowDefinition') {
            setGWFMarkdown(gwf.definition.markdown);
          } else {
            // TODO(dmchoi): handle converting between json <=> markdown
            setErrors('Cannot edit JSON markdown through this editor');
          }
          setGWFName(gwf.name);
        } else {
          // Remove gwfId from query string
          props.history.push({
            pathname: '/guided_workflows/edit',
          });
        }
        setIsLoading(false);
      };
      refresh();
    },
    [props.history],
  );

  useEffect(() => {
    // if gwfId is provided, load GWF
    if (gwfId) {
      loadGWF(gwfId);
    } else {
      setIsLoading(false);
    }
  }, [gwfId, loadGWF]);

  const handleSave = useCallback(() => {
    const saveGWF = async () => {
      if (!gwfName || !gwfMarkdown) {
        setErrors('Name and markdown must not be empty');
        return;
      }
      setIsSaving(true);
      const gwfDetails: UpdateGuidedWorkflowRequest = {
        name: gwfName,
        definition: {
          type: 'markdownWorkflowDefinition',
          markdown: gwfMarkdown,
        },
        enableRules: gwf?.enableRules ?? [],
      };
      try {
        if (gwfId) {
          await updateGWF(gwfId, gwfDetails);
        } else {
          const result = await createGWF(gwfDetails);
          const gwfId = result.id;

          // If create succeeds, update page with gwfId
          props.history.push({
            pathname: '/guided_workflows/edit',
            search: `?gwfId=${gwfId}`,
          });
        }
        SAlert.info('Saved', { timeout: 3000 });
      } catch (err) {
        SAlert.error(`Error saving gwf: ${err.response.error}`, { timeout: 3000 });
      }
      setIsSaving(false);
    };
    saveGWF();
  }, [gwf, gwfId, gwfMarkdown, gwfName, props.history]);

  return (
    <div className={classes.base}>
      <div className={classes.header}>
        <Text medium>Workflow name</Text> {requiredAsterisk}
        <TextInput value={gwfName} onChange={e => setGWFName(e.target.value)} />
      </div>
      <div className={classes.errors}>
        {errors && (
          <>
            <Text medium>Errors:</Text>
            {errors}
          </>
        )}
      </div>
      <div className={classes.markdownContainer}>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <>
            <div className={classes.markdownEditor}>
              <textarea
                placeholder="Enter guided workflow using markdown syntax"
                value={gwfMarkdown}
                onChange={e => setGWFMarkdown(e.target.value)}
              ></textarea>
            </div>
            <div className={classes.markdownRenderer}>
              <SanitizedMarkdown text={gwfMarkdown ?? ''} />
            </div>
          </>
        )}
      </div>
      <div className={classes.footer}>
        <Button onClick={handleSave} disabled={isSaving} type="primary">
          Save
        </Button>
      </div>
    </div>
  );
};

export default GuidedWorkflowEditor;
